import { useEffect, useRef } from 'react';
import { useGeoMarkupStore } from '@/store';
import { useMantineColorScheme } from '@mantine/core';
import { useI18n } from './useI18n';

/**
 * Hook to apply user preferences (language/theme) on login.
 * Uses a ref to track if preferences were already applied for the current user.
 * This prevents re-applying preferences on every render.
 */
export function useApplyUserPreferences(): void {
  // Atomic primitive selectors — subscribing to the whole `user` object would
  // re-run the effect on every store mutation that replaces it (Immer creates a
  // new reference), churning theme/language re-application during auth checks.
  const userId = useGeoMarkupStore((state) => state.auth.user?.id ?? null);
  const preferredTheme = useGeoMarkupStore(
    (state) => state.auth.user?.preferredTheme ?? null
  );
  const preferredLanguage = useGeoMarkupStore(
    (state) => state.auth.user?.preferredLanguage ?? null
  );
  const isAuthenticated = useGeoMarkupStore(
    (state) => state.auth.isAuthenticated
  );
  const { setColorScheme } = useMantineColorScheme();
  const { changeLanguage } = useI18n();

  // Track which user ID we've applied preferences for
  const appliedForUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      // Reset tracking when user logs out
      appliedForUserIdRef.current = null;
      return;
    }

    // Skip if we've already applied preferences for this user
    if (appliedForUserIdRef.current === userId) {
      return;
    }

    // Mark as applied for this user before applying
    appliedForUserIdRef.current = userId;

    // Apply language preference (validate before applying)
    if (preferredLanguage === 'en' || preferredLanguage === 'pl') {
      changeLanguage(preferredLanguage).catch(console.error);
    }

    // Apply theme preference (validate before applying)
    if (preferredTheme === 'light' || preferredTheme === 'dark') {
      setColorScheme(preferredTheme);
    }
  }, [
    isAuthenticated,
    userId,
    preferredTheme,
    preferredLanguage,
    changeLanguage,
    setColorScheme,
  ]);
}
